import { useFlags } from 'launchdarkly-react-client-sdk';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { refreshInvoiceLinks } from '../../../../api/firebase/invoices';
import {
  BILLING_FEE_TYPE,
  InvoiceStatus,
  getBillingFeeByType,
  getProcessingFeePaidByFamily,
  getWSFeeListItemDescription,
} from '../../../../helpers/invoices';
import { centsToDollars, currencyFormatter } from '../../../../helpers/utils';
import DateTime from '../../../DateTime/DateTime';
import { LoadingIndicator } from '../../../Shared/BusyIndicator';
import { DetailCard, getInvoiceTotal, getResponsibleForBilling } from './InvoiceDetailUtils';

export function InvoiceDetailDetails({ selectedInvoice, student }) {
  const { isMonetizationV2Enabled, paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer } = useFlags();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceLinks, setInvoiceLinks] = useState({
    invoicePdf: selectedInvoice?.stripe?.invoicePdf,
    invoiceUrl: selectedInvoice?.stripe?.invoiceUrl,
    lastUrlRefresh: selectedInvoice?.stripe?.lastUrlRefresh,
  });
  const responsibleForBilling = getResponsibleForBilling(student);
  const family = selectedInvoice && selectedInvoice.student && selectedInvoice.student.family;
  let email: string;

  if (selectedInvoice.billedTo) {
    email = selectedInvoice.billedTo.email;
  } else if (responsibleForBilling?.email) {
    email = responsibleForBilling.email;
  } else if (family && Object.keys(family).length > 0) {
    email = Object.keys(family)
      .filter((fam) => !!family[fam].email)
      .map((fam) => {
        // check if email exists
        if (family[fam] && family[fam].email) {
          return family[fam].email;
        }

        return null;
      })[0] as string;
  } else {
    email = t('billing.invoiceDetail.fallback.noEmail');
  }

  useEffect(() => {
    const getNewLinksIfExpired = async () => {
      const { createdAt, stripe } = selectedInvoice;
      if (!stripe?.invoiceId) return;

      const createdAtMoment = moment(createdAt);
      const lastUrlRefreshMoment = moment(invoiceLinks.lastUrlRefresh);
      // Stripe invoice links expire after 30 days. If a new one is generated, it will expire
      // after 10 days.
      const areInvoiceLinksExpired =
        createdAtMoment.isBefore(moment().subtract(29, 'days')) &&
        (!invoiceLinks.lastUrlRefresh || lastUrlRefreshMoment.isBefore(moment().subtract(9, 'days')));

      if (areInvoiceLinksExpired) {
        setIsLoading(true);
        const { data } = await refreshInvoiceLinks(stripe.invoiceId);
        setInvoiceLinks(data);
        setIsLoading(false);
      }
    };

    getNewLinksIfExpired();
  }, [invoiceLinks, selectedInvoice]);

  const revenueShareBillingFee = getBillingFeeByType(selectedInvoice?.billingFees, [BILLING_FEE_TYPE.REVENUE_SHARE]);
  const processingBillingFee = getBillingFeeByType(selectedInvoice?.billingFees, [
    BILLING_FEE_TYPE.ACH_FEE,
    BILLING_FEE_TYPE.CARD_FEE,
  ]);

  const processingFeePaidByFamily = getProcessingFeePaidByFamily(selectedInvoice?.invoiceItemList);
  const shouldShowFeeBreakdown =
    selectedInvoice?.status === InvoiceStatus.PAID ||
    selectedInvoice?.status === InvoiceStatus.PROCESSING ||
    selectedInvoice?.amountPaid > 0 ||
    selectedInvoice?.alreadyPaid;

  const renderInvoiceUrl = () => {
    return (
      <tr>
        <td data-testid="invoice-detail-invoice-link">{t('billing.invoiceDetails.invoiceLink')}</td>
        <td>
          {!isMonetizationV2Enabled ? (
            <a
              href={invoiceLinks.invoiceUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="invoice-detail-invoice-link-value"
            >
              {t('billing.invoiceDetails.invoiceLinkCaption')}
            </a>
          ) : null}
          {invoiceLinks.invoicePdf ? (
            <>
              {isMonetizationV2Enabled ? null : ' | '}
              <a
                href={invoiceLinks.invoicePdf}
                target="_blank"
                rel="noreferrer"
                data-testid="invoice-detail-invoice-pdf-link"
              >
                {isMonetizationV2Enabled
                  ? t('billing.invoiceDetails.viewInvoiceLink')
                  : t('billing.invoiceDetail.pdfLinkLabel')}
              </a>
            </>
          ) : null}
        </td>
      </tr>
    );
  };

  const renderBillingFeeRow = (fee, feeType, descriptionKey) => {
    if (!fee) return null;

    if (paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer && feeType === 'processing-fee-paid-by-family') {
      return null;
    }

    const formattedAmount = currencyFormatter(
      feeType === 'processing-fee-paid-by-family' ? fee.amount : centsToDollars(fee.amount),
      { precision: 2 }
    );
    const displayAmount = `(-${formattedAmount})`;

    return (
      <tr key={feeType}>
        <td data-testid={`invoice-detail-${feeType}`}>
          {feeType === 'processing-fee'
            ? t(getWSFeeListItemDescription(fee), {
                descriptionValue: fee.description,
                cardFeePercentage: fee.processingFee?.cardFeePercentage,
                cardFixedFee: fee.processingFee?.cardFixedFee,
                achFeePercentage: fee.processingFee?.achFeePercentage,
                achMaxFee: fee.processingFee?.achMaxFee,
              })
            : t(descriptionKey)}
        </td>
        <td data-testid={`invoice-detail-${feeType}-value`}>
          {displayAmount}
          {fee.description && (
            <span className="ws-fee-list-item-description-text">
              <span key={`${fee.amount}-${fee.description}-${fee.feeType}`}>
                {feeType === 'processing-fee'
                  ? ''
                  : `(${t(getWSFeeListItemDescription(fee), {
                      descriptionValue: fee.description,
                      cardFeePercentage: fee.processingFee?.cardFeePercentage,
                      cardFixedFee: fee.processingFee?.cardFixedFee,
                      achFeePercentage: fee.processingFee?.achFeePercentage,
                      achMaxFee: fee.processingFee?.achMaxFee,
                    })})`}
              </span>
            </span>
          )}
        </td>
      </tr>
    );
  };

  const totalFeeAmountChargedToProvider = (revenueShareBillingFee?.amount || 0) + (processingBillingFee?.amount || 0);
  const totalFeeAmountChargedToFamily = processingFeePaidByFamily?.amount || 0;
  const amountDeposited =
    selectedInvoice.total - centsToDollars(totalFeeAmountChargedToProvider) - totalFeeAmountChargedToFamily;

  const getServicePeriod = () => {
    if (selectedInvoice.servicePeriod) {
      return selectedInvoice.servicePeriod;
    }

    if (selectedInvoice.createdFromEnrollment) {
      const tuitionAndFees = selectedInvoice.student.enrollment.tuitionAndFees;
      const serviceStartDate = moment(selectedInvoice.dateServiceStart).utc();
      return tuitionAndFees.tuition.billingInterval.toLowerCase() === 'monthly'
        ? t('billing.invoiceDetail.servicePeriod.monthOf', {
            date: serviceStartDate.format('MMMM'),
          })
        : t('billing.invoiceDetail.servicePeriod.weekOf', {
            day: serviceStartDate.format('D'),
            month: serviceStartDate.format('MMMM'),
          });
    }

    return (
      <>
        <DateTime epoch={selectedInvoice.dateServiceStart} format={'MM/DD/YYYY'} /> -{' '}
        <DateTime epoch={selectedInvoice.dateServiceEnd} format={'MM/DD/YYYY'} />
      </>
    );
  };

  return (
    <DetailCard title={t('billing.invoiceDetail.metadata.title')} data-testid="invoice-detail-details-header">
      <table>
        <tbody>
          <tr>
            <td data-testid="invoice-detail-id">{t('billing.invoiceDetail.metadata.idLabel')}</td>
            <td data-testid="invoice-detail-id-value">{selectedInvoice && selectedInvoice.id}</td>
          </tr>
          <tr>
            <td data-testid="invoice-detail-created-by">{t('billing.invoiceDetail.metadata.createdLabel')}</td>
            <td data-testid="invoice-detail-created-by-value">
              {selectedInvoice?.createdBy &&
                selectedInvoice?.createdBy?.displayName &&
                selectedInvoice.createdBy.displayName + ', '}
              {selectedInvoice && selectedInvoice.createdAt && (
                <DateTime epoch={selectedInvoice.createdAt} format={'MM/DD/YYYY h:mm A'} />
              )}
            </td>
          </tr>
          {selectedInvoice?.updatedBy && (
            <tr>
              <td data-testid="invoice-detail-updated-by">{t('billing.invoiceDetail.metadata.updatedLabel')}</td>
              <td data-testid="invoice-detail-updated-by-value">
                {selectedInvoice?.updatedBy &&
                  selectedInvoice?.updatedBy?.displayName &&
                  selectedInvoice?.updatedBy?.displayName + ', '}
                {selectedInvoice && selectedInvoice?.updatedBy && selectedInvoice?.updatedBy?.dateTime && (
                  <DateTime epoch={selectedInvoice?.updatedBy?.dateTime} format={'MM/DD/YYYY h:mm A'} />
                )}
              </td>
            </tr>
          )}
          <tr>
            <td data-testid="invoice-detail-finalized-by">{t('billing.invoiceDetail.metadata.finalizedLabel')}</td>
            <td data-testid="invoice-detail-finalized-by-value">
              {selectedInvoice && selectedInvoice.createdAt && (
                <DateTime epoch={selectedInvoice.createdAt} format={'MM/DD/YYYY h:mm A'} />
              )}
            </td>
          </tr>
          <tr>
            <td data-testid="invoice-detail-email">{t('billing.invoiceDetails.emailSentTo')}</td>
            <td data-testid="invoice-detail-email-value">{email}</td>
          </tr>
          {!!selectedInvoice.dateServiceStart && !!selectedInvoice.dateServiceEnd && (
            <tr>
              <td data-testid="invoice-detail-service-interval">{t('invoiceplan.serviceInterval')}</td>
              <td data-testid="invoice-detail-service-interval-value">{getServicePeriod()}</td>
            </tr>
          )}
          {selectedInvoice.status === InvoiceStatus.PAID && selectedInvoice?.billedTo?.displayName && (
            <tr>
              <td data-testid="invoice-detail-paid-by">{t('billing.invoiceDetails.paidBy')}</td>
              <td data-testid="invoice-detail-paid-by-value">{selectedInvoice.billedTo.displayName}</td>
            </tr>
          )}
          {isLoading && <LoadingIndicator />}
          {selectedInvoice?.invoicePlanId ? (
            <tr>
              <td data-testid="invoice-detail-invoice-plan-id">
                {t('billing.invoiceDetail.metadata.recurringPlanId')}
              </td>
              <td data-testid="invoice-detail-invoice-plan-id-value">{selectedInvoice.invoicePlanId}</td>
            </tr>
          ) : null}
          {invoiceLinks.invoiceUrl ? renderInvoiceUrl() : null}
          {shouldShowFeeBreakdown && (revenueShareBillingFee || processingBillingFee || processingFeePaidByFamily) ? (
            <>
              <tr>
                <td data-testid="invoice-total">{t('billing.invoiceDetail.fees.amountPaidByFamily')}</td>
                <td data-testid="invoice-total-value">
                  {currencyFormatter(
                    getInvoiceTotal(selectedInvoice, paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer),
                    { precision: 2 }
                  )}
                </td>
              </tr>

              {renderBillingFeeRow(revenueShareBillingFee, 'ws-fee', 'billing.invoiceDetail.fees.wonderschoolFee')}
              {renderBillingFeeRow(processingBillingFee, 'processing-fee', 'billing.invoiceDetail.fees.processingFee')}
              {renderBillingFeeRow(
                processingFeePaidByFamily,
                'processing-fee-paid-by-family',
                'billing.invoiceDetail.fees.processingFeePaidByFamily'
              )}

              <tr>
                <td data-testid="invoice-detail-amount-deposited-to-provider" className="sum-top-border">
                  {t('billing.invoiceDetail.fees.amountDepositedToProvider')}
                </td>
                <td data-testid="invoice-detail-amount-deposited-to-provider-value" className="sum-top-border">
                  {currencyFormatter(amountDeposited, { precision: 2 })}
                </td>
              </tr>
            </>
          ) : null}
        </tbody>
      </table>
    </DetailCard>
  );
}
